"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import type { Session, User, SupabaseClient } from "@supabase/supabase-js";
import { createClient } from "@oxpa/shared/lib/supabase/client";

// # START_INTERFACE_PortalSessionContextType
interface PortalSessionContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  supabase: SupabaseClient | null;
}
// # END_INTERFACE_PortalSessionContextType

const PortalSessionContext = createContext<PortalSessionContextType>({
  user: null,
  session: null,
  isLoading: true,
  supabase: null,
});

export function usePortalSession() {
  return useContext(PortalSessionContext);
}

// # START_FUNCTION_PortalSessionProvider
export function PortalSessionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [supabase] = useState(() => createClient());

  useEffect(() => {
    let mounted = true;

    async function init() {
      const { data } = await supabase.auth.getSession();
      if (!mounted) return;
      setSession(data.session);
      setUser(data.session?.user ?? null);
      setIsLoading(false);
    }

    init();

    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!mounted) return;
        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);
      }
    );

    return () => {
      mounted = false;
      listener?.subscription.unsubscribe();
    };
  }, [supabase]);

  return (
    <PortalSessionContext.Provider
      value={{ user, session, isLoading, supabase }}
    >
      {children}
    </PortalSessionContext.Provider>
  );
}
// # END_FUNCTION_PortalSessionProvider